import { useEffect, useState } from "react";
import { ThemeToggle } from "@/components/ThemeToggle";

/** Tracks html[data-theme] so we can swap assets (e.g. the AI Sarva
 *  white icon becomes invisible on the cream light-theme page). */
function useTheme(): "dark" | "light" {
  const [t, setT] = useState<"dark" | "light">(() =>
    (typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "light") ? "light" : "dark",
  );
  useEffect(() => {
    const obs = new MutationObserver(() => {
      const next = document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark";
      setT(next);
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  return t;
}

/**
 * Header for IndStartups.
 *
 * Layout:
 *   [AISARVA logo — link to aisarva.com] · divider · [IndStartups wordmark BETA]
 *   ··· centre: minimal anchor nav (Journeys · Path · Map · Browse) ···
 *   right: ⌘K hint → opens the AI oracle (dispatched via CustomEvent).
 */
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-30 transition-colors duration-200 ${
        scrolled
          ? "backdrop-blur-md bg-plum-950/80 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between gap-6">
        <div className="flex items-center gap-4 min-w-0">
          <a
            href="https://aisarva.com"
            className="group flex items-center gap-2 shrink-0"
            aria-label="AI Sarva — back to aisarva.com"
          >
            <img
              src={theme === "light" ? "/brand/aisarva-icon-dark.png" : "/brand/aisarva-icon-white.png"}
              alt=""
              aria-hidden
              className="w-7 h-7 object-contain opacity-90 group-hover:opacity-100 transition-opacity"
            />
            <span className="font-display text-sm font-semibold tracking-tight text-cream-50/85 group-hover:text-cream-50 transition-colors">
              AI Sarva
            </span>
          </a>
          <span className="h-5 w-px bg-white/10" aria-hidden />
          <a href="/" className="flex items-center gap-2 min-w-0">
            <span className="font-display text-base font-semibold tracking-tight text-cream-50 truncate">
              IndStartups
            </span>
            <span className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-mono">
              beta
            </span>
          </a>
        </div>

        <nav className="hidden md:flex items-center gap-7 text-sm text-cream-100/70 font-sans">
          <a href="#journey" className="hover:text-amber-300 transition-colors">Journeys</a>
          <a href="#path"    className="hover:text-amber-300 transition-colors">Path finder</a>
          <a href="#map"     className="hover:text-amber-300 transition-colors">Map</a>
          <a href="#browse"  className="hover:text-amber-300 transition-colors">Browse</a>
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("indstartups:open-palette"))}
            className="hidden md:inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11px] font-mono text-cream-100/70 hover:text-amber-300 hover:border-amber-500/40 transition-colors"
            aria-label="Open Anveṣaṇa (Cmd/Ctrl + K)"
          >
            <kbd className="!font-sans !border-0 !bg-transparent !p-0">⌘</kbd>
            <kbd className="!font-sans !border-0 !bg-transparent !p-0">K</kbd>
            <span className="uppercase tracking-[0.2em] text-[10px]">ākāśavāṇī</span>
          </button>
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
}
